import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { getRecentCandidates } from '../utils/recentCandidates';
import { BriefcaseIcon, UserGroupIcon, ClockIcon } from '@heroicons/react/24/outline';
import WhatsAppButton from '../components/WhatsAppButton';
import CandidateStatsCards from '../components/CandidateStatsCards';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentCandidates, setRecentCandidates] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get('/api/dashboard/stats');
        setData(response.data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    setRecentCandidates(getRecentCandidates(user?.id));
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500">Loading...</div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500">Failed to load dashboard.</div>
      </Layout>
    );
  }

  const { liveJobs, candidateStats, byPosition } = data;

  const daysSince = (dateStr) => {
    if (!dateStr) return '-';
    const diff = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    if (diff === 0) return 'Today';
    if (diff === 1) return '1 day';
    return `${diff} days`;
  };

  return (
    <Layout>
      <div className="px-4 py-6 sm:px-0">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h1>

        {/* Live Jobs */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <BriefcaseIcon className="h-5 w-5 text-green-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Live Jobs</h2>
            </div>
            <span className="text-sm font-bold text-green-700 bg-green-100 px-3 py-1 rounded-full">
              {liveJobs.total} active
            </span>
          </div>
          {liveJobs.jobs.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Site</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Candidates</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Live Since</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {liveJobs.jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{job.position}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">{job.title}</td>
                    <td className="px-6 py-3 text-sm text-gray-500">{job.site || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right font-medium">{job.candidateCount}</td>
                    <td className="px-6 py-3 text-sm text-gray-500 text-right">{daysSince(job.liveSince)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-4 text-sm text-gray-500 text-center">No live jobs</div>
          )}
        </div>

        {/* Candidate Stats */}
        <CandidateStatsCards candidateStats={candidateStats} />

        {/* Side by side: Candidates by Position + Recent Candidates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Candidates by Position */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center">
              <UserGroupIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Candidates by Position</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {byPosition.map((item, idx) => (
                <div key={idx} className="px-6 py-3 flex justify-between items-center hover:bg-gray-50">
                  <span className="text-sm text-gray-900">{item.position}</span>
                  <span className="text-sm font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded">{item.count}</span>
                </div>
              ))}
              {byPosition.length === 0 && (
                <div className="px-6 py-4 text-sm text-gray-500 text-center">No data yet</div>
              )}
            </div>
          </div>

          {/* Last 10 Candidates Touched */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center">
              <ClockIcon className="h-5 w-5 text-orange-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Recently Touched</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {recentCandidates.map((c) => (
                <div
                  key={c.id}
                  className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <button
                    onClick={() => navigate(`/applicants/${c.id}`)}
                    className="flex items-center text-left flex-1 min-w-0"
                  >
                    <span className="text-sm font-medium text-blue-600">{c.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{c.position || ''}</span>
                  </button>
                  <div className="flex items-center ml-2">
                    {c.phone && <WhatsAppButton countryCode={c.countryCode} phone={c.phone} />}
                    <span className="text-xs text-gray-400 ml-2">
                      {c.touchedAt ? new Date(c.touchedAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
              {recentCandidates.length === 0 && (
                <div className="px-6 py-4 text-sm text-gray-500 text-center">No recent activity — click on a candidate to start tracking</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;

